import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { featheryDoc } from '../../../utils/browser';
import DocxToolbar from './DocxToolbar';
import { CheckIcon, CloseIcon } from './icons';
import { FEATHERY_RED, TOOLBAR_HEIGHT } from './DocxToolbar/styles';
import DocumentPanel from './DocumentPanel';
import {
  ActivePanel,
  useReviewPanelPresentation
} from './reviewPanelPresentation';
import PanelRail from './PanelRail';
import { DocxBindingsConfig, useDocxEditor } from './useDocxEditor';
import { TableDeleteImpact } from './bindings/tableDeleteGuard';
import { DocxSource } from './types';

// Re-exported for tests that import it from this module.
export { RailErrorBoundary } from './RailErrorBoundary';

/** What a host's onSave may resolve with. `file` is the public copy of the
 *  saved document (content controls stripped server-side) — the only bytes
 *  downloads are allowed to serve. */
export interface DocxSaveResult {
  file?: string | null;
  editor_file?: string | null;
  [key: string]: unknown;
}

export interface DocxEditorProps {
  /** Document to open. `buffer` when the host already fetched the bytes (e.g.
   *  an authenticated download); `url` for the component to fetch directly. */
  source?: DocxSource;
  /** Base name (no extension) used for save/download. */
  fileName?: string;
  /** Syncfusion license key — injected, never committed to this library.
   *  Optional when the Word Processor license is configured server-side. */
  licenseKey?: string;
  /** Base URL of the Document Editor web service (Feathery proxy or direct).
   *  Required to OPEN a .docx (DOCX↔SFDT conversion happens server-side). */
  serviceUrl?: string;
  /** Extra headers for serviceUrl requests (e.g. Feathery Authorization). */
  headers?: Record<string, string>[];
  readOnly?: boolean;
  /** Enables the assistant tracked-change review rail and its editor hooks. */
  reviewChanges?: boolean;
  /** Controlled reveal. When explicitly false the editor is unmounted. */
  visible?: boolean;
  /** Hide the local Download button (shown by default). */
  hideDownload?: boolean;
  /** URL of the document's public copy (content controls stripped). When set,
   *  Download saves current edits, then serves this copy (or the fresher one
   *  from the save result) — never the raw editor bytes. */
  downloadUrl?: string | null;
  /** Returns the current document as PDF bytes (converted by the host, e.g.
   *  the Feathery backend). When provided, Download becomes a DOCX/PDF menu.
   *  Current edits are saved via `onSave` before this is called. */
  onExportPdf?: () => Promise<Blob>;
  terminalAction?: 'download' | 'sign' | 'draft';
  onTerminalAction?: (saveResult?: unknown) => void | Promise<void>;
  /** Draft variant of the 'sign' terminal action (DocuSign only). When
   *  provided, Sign becomes a Send / Save as Draft menu. Same save-first flow. */
  onTerminalActionDraft?: (saveResult?: unknown) => void | Promise<void>;
  terminalActionDisabled?: boolean;
  terminalActionLoading?: boolean;
  className?: string;
  /** Bump to force a reopen of the same source URL (e.g. after regenerate). */
  openNonce?: number;
  onReady?: () => void;
  /** Live DocumentEditor instance, for programmatic control (e.g. the AI
   *  assistant drives the document directly through this). */
  onEditorReady?: (editor: any) => void;
  /** Fired with the current dirty state (true on edits, false after a save). */
  onChange?: (dirty: boolean) => void;
  onError?: (error: string) => void;
  /** Persistence boundary: receives the exported .docx. The host decides where
   *  it goes (the component never persists on its own). */
  onSave?: (
    blob: Blob
  ) => DocxSaveResult | void | Promise<DocxSaveResult | void>;
  /** Opt-in document bindings: [[...]] tokens become live fields and formulas
   *  that recalculate as the document is edited. Omitting it changes nothing. */
  bindings?: DocxBindingsConfig;
}

const overlay = {
  position: 'absolute' as const,
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  background: 'rgba(255,255,255,0.75)',
  color: '#3f3f46'
};

// Reusable Syncfusion DOCX editor: custom toolbar + inline editing in one unit
// that fills its container and manages its own overflow. Syncfusion loads from
// the CDN at runtime (no bundle bloat) and renders directly in the page (no
// iframe), so the toolbar and the AI assistant drive the editor via its API.
// I/O-agnostic — bytes in via `source`, bytes out via `onSave`; no Feathery API.
function DocxEditor({
  source,
  fileName = 'document',
  licenseKey,
  serviceUrl,
  headers,
  readOnly,
  reviewChanges = false,
  visible = true,
  hideDownload,
  downloadUrl,
  onExportPdf,
  terminalAction,
  onTerminalAction,
  onTerminalActionDraft,
  terminalActionDisabled,
  terminalActionLoading,
  className,
  openNonce,
  onReady,
  onEditorReady,
  onChange,
  onError,
  onSave,
  bindings
}: DocxEditorProps) {
  const dirtyRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // Brief feedback shown after an explicit Save — the button otherwise gives
  // no sign of whether the document actually persisted.
  // Binding-error modal shown when an action hits the export gate. Save and
  // download offer a consented escape hatch ("Save Anyway" / "Download
  // Anyway"); sign/send show it without one — informational, Close only. The
  // table-delete confirmation reuses it with its own title and a cancel hook.
  const [gateWarning, setGateWarning] = useState<{
    message: string;
    title?: string;
    confirmLabel?: string;
    confirmTitle?: string;
    proceed?: () => void;
    cancel?: () => void;
  } | null>(null);
  const [saveToast, setSaveToast] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);
  const saveToastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  // Mirror the toast into a ref so editor-event callbacks can read the current
  // type without re-subscribing.
  const saveToastRef = useRef(saveToast);
  saveToastRef.current = saveToast;
  const [terminalRunning, setTerminalRunning] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  // Debounces the DOCX download: a double-click must not race two exports
  // and two PATCH saves of the same bytes.
  const [downloading, setDownloading] = useState(false);
  // The single right-rail slot shows at most one panel, toggled from the
  // toolbar's two buttons (Changes / Sections).
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  // Pending tracked-change count, reported by the (always-mounted) rail; drives
  // the toolbar's Changes badge and whether that button is offered at all.
  const [changesCount, setChangesCount] = useState(0);

  // A single dirty transition. Shared by ordinary edits (via the hook's
  // onDirty) and the section reorder, whose programmatic open() does not
  // reliably fire the gated contentChange.
  const markDirty = useCallback(() => {
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      setDirty(true);
      onChange?.(true);
    }
  }, [onChange]);

  /**
   * Deleting a table or row that feeds calculated values elsewhere would
   * strand them (stale number, save blocked, the control itself
   * non-deletable). The guard asks here first; confirming deletes and converts
   * the dependent values to plain text in one undoable step.
   */
  const confirmTableDelete = useCallback(
    (impact: TableDeleteImpact) =>
      new Promise<boolean>((resolve) => {
        const names = impact.orphans
          .map((orphan) => `"${orphan.name}"`)
          .join(', ');
        const plural = impact.orphans.length > 1;
        const noun =
          impact.scope === 'row'
            ? 'row'
            : impact.scope === 'range'
            ? 'selection'
            : 'table';
        const label = impact.scope === 'range' ? 'Delete' : `Delete ${noun}`;
        setGateWarning({
          title: `Delete ${noun}?`,
          message: `The calculated value${
            plural ? 's' : ''
          } ${names} elsewhere in this document ${
            plural ? 'use' : 'uses'
          } numbers from this ${noun}. Deleting it keeps the current value${
            plural ? 's' : ''
          } as plain text.`,
          confirmLabel: label,
          confirmTitle: `Deletes the ${noun}; dependent calculated values become plain text`,
          proceed: () => {
            setGateWarning(null);
            resolve(true);
          },
          cancel: () => resolve(false)
        });
      }),
    []
  );

  // Non-error hint shown while the caret is on a locked control that refused an
  // edit. No timeout - it stays as long as the cursor sits on the locked cell;
  // the bindings layer resolves it when the caret moves somewhere editable or
  // leaves the editor.
  const handleLockedEdit = useCallback(() => {
    if (saveToastTimer.current) clearTimeout(saveToastTimer.current);
    setSaveToast({
      type: 'info',
      message: "This content is locked and can't be edited here."
    });
  }, []);

  // The caret left the locked spot: hide the hint now, but never clobber a save
  // success/error toast that happens to be showing.
  const handleLockedEditResolved = useCallback(() => {
    if (saveToastRef.current?.type !== 'info') return;
    setSaveToast(null);
  }, []);

  const {
    containerRef,
    editor,
    loading,
    error,
    exportDoc,
    bindings: bindingsState
  } = useDocxEditor({
    source,
    licenseKey,
    serviceUrl,
    headers,
    readOnly,
    reviewChanges,
    openNonce,
    onReady,
    onEditorReady,
    onDirty: markDirty,
    onError,
    bindings: bindings
      ? {
          ...bindings,
          confirmTableDelete,
          onLockedEdit: handleLockedEdit,
          onLockedEditResolved: handleLockedEditResolved
        }
      : bindings
  });

  // The review surface follows the document: a change set arriving presents the
  // Suggested changes panel, and resolving the last edit closes it again. Both
  // halves of that rule live in reviewPanelPresentation - the opening half used
  // to be missing, which left a landed change set invisible behind whichever
  // panel the user had open.
  useReviewPanelPresentation({
    activePanel,
    count: changesCount,
    reviewChanges: !!reviewChanges,
    setActivePanel
  });

  /**
   * Reconcile anything uncommitted before bytes leave the editor and report the
   * first blocking binding error, or null when the document is exportable.
   * commitForSave() flushes typed edits either way, so a false only means the
   * document carries an invalid binding config - not that content is missing.
   */
  const exportBlockMessage = (): string | null => {
    if (!bindingsState.ready) return null;
    if (bindingsState.commitForSave()) return null;
    const detail = bindingsState.diagnostics
      .filter((entry) => entry.severity === 'error')
      .map((entry) => entry.message);
    console.error('Feathery: document has unresolved binding errors', detail);
    return detail.length
      ? `This document cannot be saved yet: ${detail[0]}`
      : 'This document cannot be saved yet.';
  };

  /**
   * Hard gate for sign/send: refuse to export a document the engine considers
   * wrong - an invalid number or an ambiguous edit would otherwise be signed
   * as if it were fine. Shows the binding-error modal with no escape hatch:
   * these outcomes are irreversible, so there is no "Sign Anyway".
   */
  const readyToExport = (): boolean => {
    const block = exportBlockMessage();
    if (block === null) return true;
    setSaveToast(null);
    setGateWarning({ message: block });
    return false;
  };

  /**
   * Soft gate for save and download paths: same check as the hard gate, but
   * instead of refusing outright it offers an "Anyway" escape hatch - the
   * flush already happened, so proceeding just means the persisted/exported
   * document carries the invalid binding config. Signing/sending stays
   * hard-gated: those are irreversible and leave the platform.
   */
  const softGate = (
    retry: () => void,
    confirmLabel: string,
    confirmTitle: string
  ): boolean => {
    const block = exportBlockMessage();
    if (block === null) return true;
    setSaveToast(null);
    setGateWarning({
      message: block,
      confirmLabel,
      confirmTitle,
      proceed: () => {
        setGateWarning(null);
        retry();
      }
    });
    return false;
  };

  const gateDownload = (retry: () => void): boolean =>
    softGate(
      retry,
      'Download Anyway',
      'Saves and downloads the document as-is'
    );

  const gateSave = (retry: () => void): boolean =>
    softGate(retry, 'Save Anyway', 'Saves the document as-is');

  // Which editor instance the rail is showing. Derived, not state: a recreation
  // must remount the rail's boundary in the same render that swaps the editor.
  const railGenerationRef = useRef({ editor: null as any, count: 0 });
  if (railGenerationRef.current.editor !== editor)
    railGenerationRef.current = {
      editor,
      count: railGenerationRef.current.count + 1
    };
  const railGeneration = railGenerationRef.current.count;

  const triggerDownload = (blob: Blob, extension: 'docx' | 'pdf' = 'docx') => {
    const doc = featheryDoc();
    const url = URL.createObjectURL(blob);
    const a = doc.createElement('a');
    const base = fileName.replace(/\.(docx|pdf)$/i, '');
    a.href = url;
    a.download = `${base}.${extension}`;
    doc.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Persist the given (already-exported) bytes to the host. Reuses the caller's
  // blob so download/terminal flows export exactly once and save the same bytes
  // they hand back to the user.
  const saveCurrentDocument = async (blob: Blob) => {
    if (!onSave) return;
    setSaving(true);
    try {
      const result = (await onSave(blob)) as DocxSaveResult | undefined;
      dirtyRef.current = false;
      setDirty(false);
      onChange?.(false);
      return result;
    } finally {
      setSaving(false);
    }
  };

  // Flash a save toast and auto-dismiss it. Re-showing while one is already up
  // resets the timer so a second save reads as fresh feedback. Errors linger a
  // little longer than the success confirmation.
  const flashSaveToast = (
    type: 'success' | 'error' | 'info',
    message: string
  ) => {
    setSaveToast({ type, message });
    if (saveToastTimer.current) clearTimeout(saveToastTimer.current);
    saveToastTimer.current = setTimeout(
      () => setSaveToast(null),
      type === 'error' ? 5000 : 2500
    );
  };

  useEffect(
    () => () => {
      if (saveToastTimer.current) clearTimeout(saveToastTimer.current);
    },
    []
  );

  const handleSave = async (force = false) => {
    if (force) bindingsState.commitForSave();
    else if (!gateSave(() => handleSave(true))) return;
    try {
      await saveCurrentDocument(await exportDoc());
      flashSaveToast('success', 'Document saved');
    } catch (err) {
      flashSaveToast('error', 'Could not save document');
      onError?.((err as Error).message || String(err));
    }
  };

  // Re-fetch the saved public copy for download. no-store because saves reuse
  // the same object key, so the HTTP cache could otherwise serve stale bytes.
  const fetchDownloadBlob = async (url: string) => {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) throw new Error('Could not download the document');
    return await res.blob();
  };

  const handleDownload = async (force = false) => {
    if (downloading) return;
    if (force) bindingsState.commitForSave();
    else if (!gateDownload(() => handleDownload(true))) return;
    setDownloading(true);
    try {
      // Save current edits first, then serve the host's public copy — content
      // controls are stripped server-side on save, so the raw editor bytes
      // must never be what the user walks away with.
      const blob = await exportDoc();
      const saveResult =
        onSave && dirtyRef.current
          ? await saveCurrentDocument(blob)
          : undefined;
      const url = saveResult?.file ?? downloadUrl;
      // No public copy exists for standalone hosts — their exported bytes are
      // the only source.
      if (url) triggerDownload(await fetchDownloadBlob(url));
      else triggerDownload(blob);
    } catch (err) {
      onError?.((err as Error).message || String(err));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPdf = async (force = false) => {
    if (!onExportPdf || exportingPdf) return;
    if (force) bindingsState.commitForSave();
    else if (!gateDownload(() => handleDownloadPdf(true))) return;
    setExportingPdf(true);
    try {
      // The host converts the SAVED document, so persist current edits first —
      // the PDF must match what's on screen.
      const blob = await exportDoc();
      if (onSave && dirtyRef.current) await saveCurrentDocument(blob);
      triggerDownload(await onExportPdf(), 'pdf');
    } catch (err) {
      onError?.((err as Error).message || String(err));
    } finally {
      setExportingPdf(false);
    }
  };

  // Every terminal action saves the current edits first, then runs its own
  // outcome against the just-saved document.
  const saveThenRun = async (
    run: (blob: Blob, saveResult?: unknown) => void | Promise<void>,
    // Only the download outcome may bypass the gate (via "Download Anyway");
    // sign/send stay hard-gated. The flush still runs so typed edits commit.
    bypassGate = false
  ) => {
    if (terminalRunning) return;
    if (bypassGate) bindingsState.commitForSave();
    // Gated here rather than per-handler: this is the single funnel every
    // terminal action goes through, so a document the binding engine considers
    // invalid cannot be signed, sent or downloaded from any of them.
    else if (!readyToExport()) return;
    setTerminalRunning(true);
    try {
      const blob = await exportDoc();
      const saveResult =
        onSave && dirtyRef.current
          ? await saveCurrentDocument(blob)
          : undefined;
      await run(blob, saveResult);
    } catch (err) {
      onError?.((err as Error).message || String(err));
    } finally {
      setTerminalRunning(false);
    }
  };

  const handleTerminalAction = (force = false): Promise<void> | void => {
    // Terminal download gets the soft gate + "Download Anyway"; other terminal
    // outcomes keep the hard gate inside saveThenRun.
    if (
      terminalAction === 'download' &&
      !force &&
      !gateDownload(() => handleTerminalAction(true))
    )
      return;
    return saveThenRun(async (blob, saveResult) => {
      if (terminalAction === 'download') {
        // Serve the public copy, same as the toolbar Download — the editor
        // bytes carry content controls that must not leave the platform.
        const url =
          (saveResult as DocxSaveResult | undefined)?.file ?? downloadUrl;
        if (url) triggerDownload(await fetchDownloadBlob(url));
        else triggerDownload(blob);
      } else {
        await onTerminalAction?.(saveResult);
      }
    }, force && terminalAction === 'download');
  };

  // Draft variant of the 'sign' terminal action: identical save-first flow, and
  // the host sends the document to DocuSign as a draft instead of for signature.
  const handleTerminalActionDraft = () =>
    saveThenRun((_blob, saveResult) => onTerminalActionDraft?.(saveResult));

  // PDF variant of the 'download' terminal action — same save-first flow,
  // then the host-converted PDF bytes. Gating (soft, with Download Anyway)
  // lives in handleDownloadPdf.
  const handleTerminalActionPdf = async () => {
    setTerminalRunning(true);
    try {
      await handleDownloadPdf();
    } finally {
      setTerminalRunning(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className={className}
      css={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        background: '#fff'
      }}
    >
      {/* Reserve the toolbar's space until it mounts (it needs `editor`), so its
          arrival doesn't shrink the editor pane mid-load. */}
      {!editor && <div css={{ height: TOOLBAR_HEIGHT, flex: '0 0 auto' }} />}
      {editor && (
        <DocxToolbar
          editor={editor}
          // Save stays visible even alongside a terminal action so users can
          // persist edits without committing to download/sign. Arrow-wrapped:
          // handleSave takes a force flag a raw click event must not satisfy.
          onSave={onSave ? () => handleSave() : undefined}
          // Secondary Download hides only when the terminal button IS the
          // download (one Download, not two) — beside Sign/Draft it stays, so
          // configuring Sign + Download shows both. Arrow-wrapped: the handlers
          // take a `force` flag a raw DOM click event would truthily satisfy.
          onDownload={
            hideDownload || terminalAction === 'download'
              ? undefined
              : () => handleDownload()
          }
          onDownloadPdf={
            hideDownload || terminalAction === 'download' || !onExportPdf
              ? undefined
              : () => handleDownloadPdf()
          }
          downloadBusy={exportingPdf || downloading}
          terminalAction={terminalAction}
          onTerminalAction={
            onTerminalAction ? () => handleTerminalAction() : undefined
          }
          onTerminalActionPdf={
            terminalAction === 'download' && onExportPdf
              ? handleTerminalActionPdf
              : undefined
          }
          onTerminalActionDraft={
            terminalAction === 'sign' && onTerminalActionDraft
              ? handleTerminalActionDraft
              : undefined
          }
          terminalActionDisabled={
            !!terminalActionDisabled || saving || terminalRunning
          }
          terminalActionLoading={!!terminalActionLoading || terminalRunning}
          saving={saving}
          dirty={dirty}
          readOnly={readOnly}
        />
      )}
      <div css={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <div css={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {/* Syncfusion mounts its editor into this element. */}
          <div
            ref={containerRef}
            css={{
              width: '100%',
              height: '100%',
              // The theme gives the editor root its own 1px border; the side
              // panel / edge rail draw the right-hand seam themselves, so the
              // editor's copy would double it (visibly thicker when the panel
              // is collapsed and the rail sits flush against the editor).
              '& .e-de-ctn': { borderRight: 'none' },
              // Syncfusion's status-bar page control renders the "Page" label,
              // the number input, and "of N" on a line but the input box is
              // taller than the text and sits low. Flex-center the whole
              // control and normalize the input box (height/line-height/
              // margin) so all three align on one baseline. Scoped to this
              // editor's DOM.
              '& .e-de-ctnr-pagenumber': {
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4
              },
              '& .e-de-ctnr-pagenumber .e-input-group': {
                margin: 0,
                alignSelf: 'center'
              },
              '& .e-de-pagenumber-input': {
                height: 22,
                minHeight: 22,
                lineHeight: '22px',
                padding: '0 4px',
                margin: 0,
                boxSizing: 'border-box',
                textAlign: 'center'
              },
              '& .e-de-pagenumber-text': {
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: '22px'
              }
            }}
          />
          {loading && !error && <div css={overlay}>Loading document…</div>}
          {error && <div css={{ ...overlay, color: '#dc2626' }}>{error}</div>}
        </div>
        {/* Shared side panel; its title follows the rail icon that opened it
            (Suggested changes · Sections). Stays mounted while review is on so
            its pending count keeps the edge-rail badge live; collapses to zero
            width when no panel is open. */}
        {editor && (
          <DocumentPanel
            editor={editor}
            open={activePanel !== null}
            tab={activePanel ?? 'sections'}
            onClose={() => setActivePanel(null)}
            reviewChanges={!!reviewChanges}
            onChangesCount={setChangesCount}
            markDirty={markDirty}
            boundaryKey={`${railGeneration}:${openNonce ?? 0}`}
          />
        )}
        {/* Slim edge rail on the far right: one icon per side panel. Always
            present so a panel is one click away and future panels can slot in. */}
        {editor && (
          <PanelRail
            activePanel={activePanel}
            showChanges={!!reviewChanges}
            changesCount={changesCount}
            onToggle={(panel) =>
              setActivePanel((p) => (p === panel ? null : panel))
            }
          />
        )}
      </div>
      {/* Binding-error prompt: a blocking modal. Portaled to the document body
          (same pattern as the toolbar menus) so the backdrop covers the whole
          page — nothing proceeds until the user picks the Anyway action
          (proceed despite invalid binding config) or cancels (button or Esc).
          Backdrop clicks are inert on purpose: the choice must be explicit. */}
      {gateWarning &&
        createPortal(
          <div
            css={{
              position: 'fixed',
              inset: 0,
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(24, 24, 27, 0.45)'
            }}
          >
            <div
              role='alertdialog'
              aria-modal='true'
              aria-label={gateWarning.title ?? 'Document has binding errors'}
              tabIndex={-1}
              ref={(node) => node?.focus()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  gateWarning.cancel?.();
                  setGateWarning(null);
                }
              }}
              css={{
                width: 440,
                maxWidth: 'calc(100% - 48px)',
                boxSizing: 'border-box',
                padding: '20px 24px',
                borderRadius: 10,
                background: '#fff',
                color: '#27272a',
                border: '1px solid #e4e4e7',
                fontSize: 14,
                lineHeight: 1.5,
                boxShadow:
                  '0 0 0 1px rgb(0 9 50 / 4%), 0 24px 48px -12px rgb(0 9 50 / 25%)',
                outline: 'none'
              }}
            >
              <div
                css={{
                  fontSize: 15,
                  fontWeight: 600,
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <span
                  aria-hidden
                  css={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    flex: '0 0 auto',
                    background: '#fef3c7',
                    color: '#b45309',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 700
                  }}
                >
                  !
                </span>
                {gateWarning.title ?? 'Document has binding errors'}
              </div>
              <div
                css={{
                  marginBottom: gateWarning.proceed ? 18 : 8,
                  color: '#3f3f46'
                }}
              >
                {gateWarning.message}
              </div>
              {!gateWarning.proceed && (
                <div css={{ marginBottom: 18, color: '#71717a', fontSize: 13 }}>
                  Fix these errors before signing or sending the document.
                </div>
              )}
              <div
                css={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
              >
                <button
                  type='button'
                  onClick={() => {
                    gateWarning.cancel?.();
                    setGateWarning(null);
                  }}
                  css={{
                    padding: '8px 14px',
                    borderRadius: 6,
                    border: '1px solid #e4e4e7',
                    background: '#fff',
                    color: '#3f3f46',
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >
                  {gateWarning.proceed ? 'Cancel' : 'Close'}
                </button>
                {gateWarning.proceed && (
                  <button
                    type='button'
                    onClick={gateWarning.proceed}
                    title={gateWarning.confirmTitle}
                    css={{
                      padding: '8px 14px',
                      borderRadius: 6,
                      border: '1px solid transparent',
                      background: FEATHERY_RED,
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    {gateWarning.confirmLabel}
                  </button>
                )}
              </div>
            </div>
          </div>,
          featheryDoc().body
        )}
      {/* Save feedback. Positioned over the editor, bottom-center, and
          auto-dismissed. Styled to match the Feathery dashboard toast: white
          surface, thin zinc border, dark text, fixed width. The tick sits at
          the far left (task-view style) while the copy stays centered. */}
      {saveToast && (
        <div
          role='status'
          aria-live={saveToast.type === 'error' ? 'assertive' : 'polite'}
          css={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 356,
            maxWidth: 'calc(100% - 32px)',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: 12,
            borderRadius: 6,
            background: '#fff',
            color: '#27272a',
            border: '1px solid #e4e4e7',
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.4,
            boxShadow:
              '0 0 0 1px rgb(0 9 50 / 3%), 0 12px 32px -16px rgb(0 9 50 / 12%)',
            zIndex: 20,
            pointerEvents: 'none'
          }}
        >
          {saveToast.type === 'success' && (
            <CheckIcon
              width={16}
              height={16}
              css={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            />
          )}
          {saveToast.type === 'error' && (
            <CloseIcon
              width={16}
              height={16}
              css={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#ef4444'
              }}
            />
          )}
          {saveToast.message}
        </div>
      )}
    </div>
  );
}

export default DocxEditor;
